import { Link } from "react-router-dom";
import { PROGRAMS, CONTACT_EMAIL } from "../lib/zocData";
import { Container } from "../lib/ui";

export const Footer = () => {
  return (
    <footer data-testid="main-footer" className="bg-slate-50 border-t border-slate-200">
      <Container className="pt-16 pb-10">
        <div className="grid gap-12 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
          <div>
            <span className="inline-flex items-center gap-2.5">
              <img src="/zoc-mark.png" alt="ZOC Medical" className="h-8 w-auto object-contain" />
              <span className="font-display text-lg font-extrabold text-[#0B2A4E] tracking-tight">ZOC Medical Inc</span>
            </span>
            <div className="mt-5 h-1 w-24 rounded-full zoc-gradient-bar" />
            <p className="mt-5 font-display text-lg font-semibold text-[#0B2A4E]">
              Smarter Care Management. Better Outcomes. Healthier Lives.
            </p>
            <p className="mt-3 max-w-sm text-sm text-slate-500 leading-relaxed">
              A care-management platform and a dedicated coordination team — helping practices deliver
              continuous, compliant care between every visit.
            </p>
            <Link
              to="/contact"
              data-testid="footer-cta"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#0B2A4E] text-white px-5 py-2.5 text-sm font-medium shadow-sm transition-colors duration-200 hover:bg-[#08203B]"
            >
              Book a demo →
            </Link>
          </div>

          <FooterCol title="Solutions">
            <FLink to="/platform">The Platform</FLink>
            <FLink to="/services">Care Coordination</FLink>
            <FLink to="/project-management">Project Management</FLink>
            <FLink to="/medical-billing-rcm">Medical Billing &amp; RCM</FLink>
            <FLink to="/programs">All Programs</FLink>
            {PROGRAMS.map((p) => (
              <FLink key={p.slug} to={`/programs/${p.slug}`}>{p.code}</FLink>
            ))}
          </FooterCol>

          <FooterCol title="Resources">
            <FLink to="/insights">Insights (Blog)</FLink>
            <FLink to="/resources">Resource Library</FLink>
            <FLink to="/fit">Find Your Fit</FLink>
            <FLink to="/behavioral-health">Behavioral Health</FLink>
            <FLink to="/compliance">Compliance & Billing</FLink>
            <FLink to="/implementation">Implementation</FLink>
            <FLink to="/calculator">Sustainability Planner</FLink>
          </FooterCol>

          <FooterCol title="Contact">
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="footer-email" className="block py-1.5 text-sm text-slate-500 hover:text-[#0B2A4E] transition-colors duration-200">
              {CONTACT_EMAIL}
            </a>
            <FLink to="/contact">Book a Demo</FLink>
          </FooterCol>
        </div>

        <div className="mt-14 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-slate-400">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <p>© {new Date().getFullYear()} ZOC Medical Inc. All rights reserved.</p>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#0B2A4E] transition-colors duration-200">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#0B2A4E] transition-colors duration-200">Terms of Service</Link>
          </div>
          <p className="max-w-xl leading-relaxed">
            Revenue figures are illustrative and not financial or billing advice. Verify all CMS codes and concurrent-billing rules for the current year before billing.
          </p>
        </div>
        <p data-testid="footer-sms-disclosure" className="mt-4 text-[11px] text-slate-400 leading-relaxed max-w-4xl">
          Text messaging from ZOC Medical Inc: with your consent we may send non-marketing messages (account alerts, reminders and service notifications) and, only with separate marketing consent, marketing messages (offers, updates and tips). Message frequency may vary. Message &amp; data rates may apply. Reply STOP to opt out. Reply HELP for help. Consent is not a condition of purchase. See our <Link to="/privacy" className="text-zoc-teal hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-zoc-teal hover:underline">Terms of Service</Link>.
        </p>
      </Container>
    </footer>
  );
};

const FooterCol = ({ title, children }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400 mb-3">{title}</p>
    <div>{children}</div>
  </div>
);

const FLink = ({ to, children }) => (
  <Link to={to} className="block py-1.5 text-sm text-slate-500 hover:text-[#0B2A4E] transition-colors duration-200">{children}</Link>
);
